import Core from "@/core/Core";

abstract class BasePageFilter {
    protected abstract storageKey: string;

    /**
     * Имена полей, зафиксированных пресетом. Пользователь не может их
     * менять через UI (контролы должны быть disabled), а reset/deserialize
     * не имеют права их перетирать. В localStorage не сохраняются — пресет
     * задаётся снаружи (страницей/воронкой) при каждом создании фильтра.
     */
    protected lockedFields: Set<string> = new Set();

    /**
     * Создать экземпляр и загрузить из localStorage
     */
    static fromLocalStorage<T extends BasePageFilter>(this: new () => T): T {
        const instance = new this();
        instance.loadFromLocalStorage();
        return instance;
    }

    /**
     * Зафиксировать значения полей и заблокировать их от изменения.
     * Возвращает this для chain-вызовов.
     */
    applyPreset(preset: Partial<this>): this {
        for (const key of Object.keys(preset) as Array<keyof this>) {
            this[key] = preset[key] as this[keyof this];
            this.lockedFields.add(key as string);
        }
        return this;
    }

    /**
     * Проверка, заблокировано ли поле пресетом.
     */
    isLocked(field: keyof this): boolean {
        return this.lockedFields.has(field as string);
    }

    /**
     * Применить фильтры
     * Сохраняет в localStorage (другие вкладки получат storage event)
     */
    apply(): void {
        const serialized = this.serialize();
        localStorage.setItem(this.storageKey, serialized);
    }

    /**
     * Сбросить фильтры к дефолтным значениям.
     * В реализации обязательно проверять isLocked() для каждого поля
     * перед обнулением.
     */
    abstract reset(): void;

    /**
     * Параметры фильтра для отправки в запросе
     */
    abstract getRequestParams(): Record<string, any>;

    /**
     * Сериализация фильтров для сохранения
     * Автоматически убирает подчёркивания из приватных полей.
     * lockedFields не сохраняем — пресет задаётся снаружи, не из стораджа.
     */
    protected serialize(): string {
        const data: Record<string, any> = {};

        for (const key in this) {
            if (key === 'storageKey' || key === 'lockedFields') {
                continue;
            }
            if (this.hasOwnProperty(key)) {
                // Убираем подчёркивание из ключа для JSON
                const jsonKey = key.startsWith('_') ? key.slice(1) : key;

                // залоченные пресетом поля не сохраняем — пресет задаётся снаружи
                if (this.isLocked(jsonKey as keyof this) === false) {
                    data[jsonKey] = Core.utils.simplifyObjectForServer(this[key]);
                }
            }
        }

        return JSON.stringify(data);
    }

    /**
     * Десериализация фильтров из localStorage.
     * В реализации обязательно проверять isLocked() для каждого поля
     * перед присвоением — залоченные пресетом значения нельзя перетирать
     * данными из стораджа.
     */
    protected abstract deserialize(json: string): void;

    /**
     * Загрузка сохранённых фильтров из localStorage
     */
    private loadFromLocalStorage(): void {
        let stored = localStorage.getItem(this.storageKey);
        // если в сторе ничего не нашли, то ничего страшного, всё равно deserialize нужен
        if (!stored) stored = '{}';

        this.deserialize(stored);
    }
}

export default BasePageFilter;